"use client";

import React from "react";
import { Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
function InnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 py-12 text-center animate-in fade-in duration-500">
        <div className="rounded-full bg-primary/20 p-3">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-2xl font-bold text-foreground">Inspection Scheduled</h3>
          <p className="text-muted-foreground font-serif">{successMessage}</p>
        </div>
      </div>;
  }
  const inputStyles = "border-2 border-border rounded-none bg-background px-4 shadow-none focus-visible:ring-0 focus-visible:border-primary text-base py-2 h-12 text-foreground";
  return <Form onSubmit={handleSubmit} className="space-y-8 w-full max-w-md mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">First Name</FormLabel>
              <FormControl>
                <Input placeholder="First Name" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Last Name" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="Email Address" className={inputStyles} {...field} />
            </FormControl>
            <FormDescription className="text-xs font-default">We'll contact you within 24 hours</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="Phone Number" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm text-muted-foreground font-default">Service Required</FormLabel>
            <FormControl>
              <select className={`w-full appearance-none outline-none transition-colors ${inputStyles} text-foreground cursor-pointer font-default`} {...field}>
                <option value="" disabled>Select a service</option>
                <option value="General Pest">General Pest Control</option>
                <option value="Termite">Termite Treatment</option>
                <option value="Mosquito">Mosquito Control</option>
                <option value="Spider">Spider Control</option>
                <option value="Inspection">Free Inspection</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_terms" rules={{
      required: "You must agree to receive communications"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-none border-2 border-primary text-primary focus:ring-primary accent-primary" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-muted-foreground cursor-pointer font-default">
                I agree to receive service updates from Peace River
              </FormLabel>
              <FormDescription className="text-xs font-default">
                Your data is protected by our privacy policy
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive border-2 border-destructive bg-destructive/10 rounded-none font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform h-14 text-lg font-bold tracking-wider rounded-full uppercase">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Processing...
          </> : "Get A Free Quote"}
      </Button>
    </Form>;
}
export default function JoinThe1220Family() {
  return <section data-section-id="226877" id="get-a-quote" className="bg-background text-foreground relative">
      <div className="absolute inset-0 bg-primary/5" aria-hidden="true" />
      
      <div className="relative mx-auto max-w-3xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center mb-12 space-y-4">
          <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Start Your Seasonal Protection
          </h2>
          <p className="text-xl text-muted-foreground font-serif">
            Reliable pest control for Punta Gorda and surrounding areas.
          </p>
        </div>

        <div className="bg-card backdrop-blur-none p-8 sm:p-12 rounded-none shadow-none border-2 border-border">
          <FormProvider formKey="quote_form_mosquito_control" formId="quote_form_mosquito_control" sectionName="get-a-quote" defaultValues={{
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          interest: "",
          agree_terms: false
        }} validationRules={{
          first_name: {
            required: true
          },
          last_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          agree_terms: {
            required: true
          }
        }} submitText="Get A Free Quote" successMessage="Thank you for reaching out. A Peace River specialist will contact you shortly to schedule your free inspection.">
            <InnerForm />
          </FormProvider>
        </div>
      </div>
      <Toaster />
    </section>;
}